import type { Brand } from "./Brand";

/**
 * A tenant's Firestore DOCUMENT id — nominally distinct from its guid.
 *
 * These are both random-looking strings and they are NOT interchangeable, but
 * for a long time both were plain `string`, so the compiler happily accepted
 * one where the other belonged. That confusion is a recurring defect class in
 * this codebase, not a one-off:
 *
 *   - `integrations` browser reads were denied for ~3.5 months because
 *     `firestore.rules` gates on the DOC id (custom claims are keyed by doc id:
 *     `request.auth.token.tenants`) while every client query filtered on the
 *     guid. Renaming the parameter caught nothing — both sides were `string`.
 *   - `firestore.rules` `email_identities` passes `resource.data.tenantGuid`
 *     into `hasTenantGrant(tenantDocId)`, which therefore never matches.
 *   - `menuSyncService` checks a doc-id prefix against a guid, so the check
 *     silently never fires (see CLAUDE.md: "prefixes live on doc ids, never
 *     guids").
 *
 * Branding ONE side is enough to stop the swap: a plain `string` — which is
 * what `tenant.guid` is — is not assignable to `TenantDocId`, so passing a guid
 * where a doc id belongs is now a compile error rather than a silent
 * PERMISSION_DENIED in production.
 *
 * Construct one only where a doc id genuinely originates (a Firestore document
 * reference / `DocumentWithId.docId`), via {@link asTenantDocId}. Do not sprinkle
 * the cast at call sites — that just relocates the bug.
 */
export type TenantDocId = Brand<string, "TenantDocId">;

/**
 * Assert that a string is a tenant document id.
 *
 * Call this ONLY at the boundary where the value comes off a Firestore
 * document. Everywhere downstream should already be typed `TenantDocId`.
 */
export const asTenantDocId = (docId: string): TenantDocId => docId as TenantDocId;
